// Loaded by several sections on one page; the block scope keeps a second copy from
// redeclaring the classes (customElements.get guards the definitions).
{
class ProductCardForm extends HTMLElement {
  connectedCallback() {
    this.input = this.querySelector('[data-variant-id]');
    this.submit = this.querySelector('.card-form__submit');
    this.sizes = [...this.querySelectorAll('.card-form__size')];
    this.variants = JSON.parse(this.querySelector('[data-variants]')?.textContent || '[]');

    this.form = this.querySelector('form');
    this.dropdown = this.querySelector('[data-size-select]');

    this.sizes.forEach((button) => {
      button.addEventListener('click', () => this.select(button));
    });

    this.dropdown?.addEventListener('click', (event) => {
      const option = event.target.closest('[data-size-option]');
      if (!option || option.disabled) return;
      const button = this.sizes.find((size) => size.dataset.value === option.dataset.sizeOption);
      if (button) this.select(button);
      this.dropdown.open = false;
    });

    this.onOutsideClick = (event) => {
      if (this.dropdown?.open && !this.dropdown.contains(event.target)) this.dropdown.open = false;
    };
    document.addEventListener('click', this.onOutsideClick);

    this.form?.addEventListener('submit', (event) => {
      if (this.input.value) return;
      event.preventDefault();
      event.stopPropagation();
      this.open();
    });
  }

  disconnectedCallback() {
    document.removeEventListener('click', this.onOutsideClick);
  }

  open() {
    if (this.dropdown && this.dropdown.offsetParent) {
      this.dropdown.classList.remove('is-missing');
      void this.dropdown.offsetWidth;
      this.dropdown.classList.add('is-missing');
      this.dropdown.open = true;
      return;
    }
    if (this.form.classList.contains('is-open')) return;
    this.form.classList.add('is-open');
    this.sizes.find((size) => !size.disabled)?.focus({ preventScroll: true });
  }

  select(button) {
    if (button.disabled) return;
    this.sizes.forEach((size) => size.setAttribute('aria-checked', String(size === button)));
    if (this.dropdown) {
      this.dropdown.classList.remove('is-missing');
      this.dropdown.querySelectorAll('[data-size-option]').forEach((option) => {
        option.setAttribute('aria-selected', String(option.dataset.sizeOption === button.dataset.value));
      });
      const current = this.dropdown.querySelector('[aria-selected="true"]');
      if (current) this.dropdown.querySelector('[data-size-select-label]').textContent = current.textContent.trim();
    }

    const variant = this.variants.find((v) => v.value === button.dataset.value && v.available);
    if (!variant) return;

    this.input.value = variant.id;
    this.submit.disabled = false;
    this.submit.removeAttribute('aria-disabled');
    delete this.submit.dataset.needsSize;
    this.submit.classList.remove('button--outline');
    const size = button.textContent.trim();
    this.submit.textContent = this.submit.dataset.labelAdd.replace('__PRICE__', variant.price).replace('__SIZE__', size);
    const label = this.querySelector('[data-label-selected]');
    if (label) label.textContent = label.dataset.labelSelected.replace('__SIZE__', size);
  }
}

if (!customElements.get('product-card-form')) customElements.define('product-card-form', ProductCardForm);

class CardGallery extends HTMLElement {
  connectedCallback() {
    this.track = this.querySelector('.product-card__track');
    this.counters = [...this.querySelectorAll('[data-gallery-current]')];
    this.prev = this.querySelector('[data-gallery-prev]');
    this.next = this.querySelector('[data-gallery-next]');
    if (!this.track) return;

    this.prev?.addEventListener('click', () => this.go(-1));
    this.next?.addEventListener('click', () => this.go(1));
    this.track.addEventListener('scroll', () => this.update(), { passive: true });
  }

  get count() {
    return this.track.children.length;
  }

  get index() {
    return Math.round(this.track.scrollLeft / (this.track.clientWidth || 1));
  }

  go(direction) {
    const target = Math.min(Math.max(this.index + direction, 0), this.count - 1);
    this.track.scrollTo({ left: target * this.track.clientWidth, behavior: 'smooth' });
  }

  update() {
    const index = this.index;
    this.counters.forEach((counter) => (counter.textContent = index + 1));
    if (this.prev) this.prev.disabled = index <= 0;
    if (this.next) this.next.disabled = index >= this.count - 1;
  }
}

if (!customElements.get('card-gallery')) customElements.define('card-gallery', CardGallery);

class ProductSlider extends HTMLElement {
  connectedCallback() {
    this.track = this.querySelector('[data-slider-track]');
    this.prev = this.querySelector('[data-slider-prev]');
    this.next = this.querySelector('[data-slider-next]');
    if (!this.track) return;

    this.prev?.addEventListener('click', () => this.move(-1));
    this.next?.addEventListener('click', () => this.move(1));
    this.track.addEventListener('scroll', () => this.update(), { passive: true });

    this.resizeObserver = new ResizeObserver(() => {
      this.alignArrows();
      this.update();
    });
    this.resizeObserver.observe(this.track);

    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    // A swipe or a scroll by the visitor pauses the autoplay, so it never fights their finger.
    this.track.addEventListener('touchstart', () => this.pause(8000), { passive: true });
    this.track.addEventListener(
      'scroll',
      () => {
        if (!this.moving) this.pause(6000);
      },
      { passive: true }
    );
    this.addEventListener('mouseenter', () => this.pause());
    this.addEventListener('mouseleave', () => this.play());
    this.addEventListener('focusin', () => this.pause());
    this.addEventListener('focusout', () => this.play());
    this.addEventListener('pointerdown', () => this.pause(8000), { passive: true });
    this.prev?.addEventListener('click', () => this.pause(8000));
    this.next?.addEventListener('click', () => this.pause(8000));

    this.visibility = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) this.play();
      else this.pause();
    });
    this.visibility.observe(this);
  }

  disconnectedCallback() {
    this.resizeObserver?.disconnect();
    this.visibility?.disconnect();
    this.pause();
  }

  play() {
    this.pause();
    this.timer = window.setInterval(() => this.autoAdvance(), 4000);
  }

  pause(resumeAfter) {
    window.clearInterval(this.timer);
    window.clearTimeout(this.resumeTimer);
    if (resumeAfter) this.resumeTimer = window.setTimeout(() => this.play(), resumeAfter);
  }

  autoAdvance() {
    const max = this.track.scrollWidth - this.track.clientWidth;
    if (max <= 2) return;
    this.markMoving();
    if (this.track.scrollLeft >= max - 2) this.track.scrollTo({ left: 0, behavior: 'smooth' });
    else this.move(1);
  }

  // Scrolls started by the slider itself must not count as the visitor scrolling.
  markMoving() {
    this.moving = true;
    window.clearTimeout(this.movingTimer);
    this.movingTimer = window.setTimeout(() => (this.moving = false), 900);
  }

  get step() {
    const item = this.track.firstElementChild;
    if (!item) return this.track.clientWidth;
    const gap = parseFloat(getComputedStyle(this.track).columnGap) || 0;
    return item.getBoundingClientRect().width + gap;
  }

  move(direction) {
    this.markMoving();
    this.track.scrollBy({ left: direction * this.step, behavior: 'smooth' });
  }

  update() {
    const max = this.track.scrollWidth - this.track.clientWidth;
    this.toggleAttribute('data-no-scroll', max <= 2);
    if (this.prev) this.prev.disabled = this.track.scrollLeft <= 2;
    if (this.next) this.next.disabled = this.track.scrollLeft >= max - 2;
  }

  alignArrows() {
    const media = this.track.querySelector('.product-card__media');
    if (media) this.style.setProperty('--arrow-top', `${media.offsetHeight / 2}px`);
  }
}

if (!customElements.get('product-slider')) customElements.define('product-slider', ProductSlider);


document.addEventListener('click', (event) => {
  const tab = event.target.closest('[data-collection-tabs] [data-tab]');
  if (!tab) return;
  const section = tab.closest('.featured-collection');
  section.querySelectorAll('[data-collection-tabs] [data-tab]').forEach((item) => {
    const active = item === tab;
    item.classList.toggle('is-active', active);
    item.setAttribute('aria-selected', String(active));
  });
  section.querySelectorAll('[data-tab-panel]').forEach((panel) => {
    panel.hidden = panel.dataset.tabPanel !== tab.dataset.tab;
  });
});
}
